import React, { useCallback, useEffect, useRef, useState } from 'react';
import ClickAwayListener from '@mui/base/ClickAwayListener';
import Popper, { PopperProps } from '@mui/material/Popper';
import { LocalizationProvider, PickersDay } from '@mui/lab';
import { format as formatDate, isSameDay, isValid, parse } from 'date-fns';
import AdapterDateFns from '@mui/lab/AdapterDateFns';

import { Icons } from 'Icon';
import { TextField, TextFieldProps } from '../TextField';
import { StaticDatePicker, StaticDatePickerProps } from 'StaticDatePicker';

import { CalendarContainer, Container } from './DatePickerInput.styled';

const allowedFormats = [
  'PPPP',
  'MM/dd',
  'dd/MM',
  'MM/dd/yyyy',
  'MM/dd/yy',
  'MM-dd',
  'dd-MM',
  'MM-dd-yyyy',
  'MM-dd-yy',
  'yyyy-MM-dd',
  'yy-MM-dd',
  'yyyy/MM/dd',
  'yy/MM/dd',
  'EEEEMMMMdd',
  'EEELLLdd',
  'EEEEMMMMddyyyy',
  'EEEEMMMMddyy',
  'EEELLLddyyyy',
  'EEELLLddyy',
];

export interface DatePickerInputProps extends Omit<StaticDatePickerProps, 'renderInput' | 'value'> {
  value: Date | null;
  open?: boolean;
  inputProps?: TextFieldProps;
  zIndex?: number;
  className?: string;
  PopperProps?: Omit<PopperProps, 'open'>;
  allowNull?: boolean;
  disableScroll?: boolean;
}

export function DatePickerInput({
  inputFormat,
  value = null,
  onChange,
  open,
  onOpen = () => {},
  onClose = () => {},
  inputProps,
  zIndex = 1300,
  className,
  PopperProps = {},
  allowNull = false,
  disableScroll = false,
  ...restDatePickerProps
}: DatePickerInputProps) {
  const { label = '', fullWidth = false, ...restInputProps } = inputProps ?? {};
  const format = inputFormat ?? 'MM/dd/yyyy';
  const [isCalendarOpen, setCalendarOpen] = useState<boolean>(false);
  const textboxRef = useRef(null);
  const calendarContainerRef = useRef<HTMLElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const [datePickerInputValue, setDatePickerInputValue] = useState<string>(() => {
    return isValid(value) ? formatDate(value!, format) : '';
  });
  const isOpenControlled = open !== undefined;
  const showCalendar = Boolean(isOpenControlled ? open : isCalendarOpen);
  const openCalendar = isOpenControlled ? onOpen : () => setCalendarOpen(true);
  const containerRef = useRef(null);

  const closeCalendar = useCallback(() => {
    if (isOpenControlled) {
      onClose();
    } else {
      setCalendarOpen(false);
    }
  }, [onClose, isOpenControlled]);

  const onCloseDatePicker = useCallback(() => {
    const defaultDate = isValid(value) ? value : new Date();
    if (!(!datePickerInputValue && allowNull)) setDatePickerInputValue(formatDate(defaultDate!, format));
    closeCalendar();
  }, [value, format, closeCalendar, datePickerInputValue, allowNull]);

  useEffect(() => {
    function onKeyDownEvent(e: KeyboardEvent): void {
      if (showCalendar && (e.key === 'Tab' || e.key === 'Escape')) {
        onCloseDatePicker();
      }
    }

    window.addEventListener('keydown', onKeyDownEvent);

    return () => {
      window.removeEventListener('keydown', onKeyDownEvent);
    };
  }, [onCloseDatePicker, showCalendar]);

  useEffect(() => {
    if (isValid(value)) {
      setDatePickerInputValue(formatDate(value!, format));
    } else {
      setDatePickerInputValue('');
    }
  }, [value, format]);

  const preventScroll = useCallback(
    (e: KeyboardEvent): void => {
      if (disableScroll && showCalendar) {
        e.preventDefault();
      }
    },
    [disableScroll, showCalendar],
  );

  useEffect(() => {
    window.addEventListener('scroll', preventScroll);
    window.addEventListener('mousewheel', preventScroll, {
      passive: false,
    });

    return () => {
      window.removeEventListener('scroll', preventScroll);
      window.removeEventListener('mousewheel', preventScroll);
    };
  }, [preventScroll]);

  function onClickAway(e: MouseEvent): void {
    if (
      showCalendar &&
      e.target !== textboxRef.current &&
      e.target !== labelRef.current &&
      e.target !== iconRef.current &&
      !iconRef.current?.contains(e.target as Node)
    ) {
      onCloseDatePicker();
    }
  }

  function onIconClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    e.stopPropagation();
    if (showCalendar) {
      closeCalendar();
      setInputFormattedValue();
    } else {
      openCalendar();
    }
  }

  function onDatePickerChange(newValue: Date): void {
    onChange(newValue);
    setDatePickerInputValue(formatDate(newValue, format));
    closeCalendar();
  }

  function setDateFromText(date: string): void {
    const cleanTextDate = date.replace(/[\s,]/g, '');
    for (let i = 0; i < allowedFormats.length; i++) {
      if (allowNull && date === '') {
        onChange(date);
        break;
      }
      const parsedDate = parse(cleanTextDate, allowedFormats[i], new Date());
      // 1900 is the minor year supported by the Mui Calendar
      if (isValid(parsedDate) && !isSameDay(parsedDate, value!) && parsedDate.getFullYear() > 1899) {
        onChange(parsedDate);
        break;
      }
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setDateFromText(e.target.value);
    setDatePickerInputValue(e.target.value);
  }

  function onInputBlur() {
    setInputFormattedValue();
  }

  function setInputFormattedValue() {
    if (!(!datePickerInputValue && allowNull) && isValid(value)) {
      setDatePickerInputValue(formatDate(value!, format));
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      closeCalendar();
      setInputFormattedValue();
    }
  }
  class CustomString extends String {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    charAt(_: number): any {
      return {
        toUpperCase: () => this.valueOf(),
      };
    }
  }

  const weekDays: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'thu', 'Fri', 'Sat'];
  const customWeekDays = weekDays.map((day) => new CustomString(day) as string);

  class DateAdapter extends AdapterDateFns {
    getWeekdays = () => customWeekDays;
  }

  return (
    <Container className={className} fullWidth={fullWidth} ref={containerRef}>
      <TextField
        InputLabelProps={{ ref: labelRef }}
        fullWidth={fullWidth}
        icon={Icons.calendar}
        iconProps={{
          onClick: onIconClick,
          style: { cursor: 'pointer' },
          ref: iconRef,
        }}
        inputRef={textboxRef}
        label={label}
        onBlur={onInputBlur}
        onChange={onInputChange}
        onClick={openCalendar}
        onFocus={openCalendar}
        onKeyDown={onKeyDown}
        value={datePickerInputValue}
        variant="outlined"
        {...restInputProps}
      />
      <ClickAwayListener mouseEvent="onMouseDown" onClickAway={onClickAway}>
        <Popper
          placement="bottom-start"
          {...PopperProps}
          anchorEl={containerRef.current}
          open={showCalendar}
          style={{ zIndex }}
        >
          <CalendarContainer ref={calendarContainerRef} role="presentation" tabIndex={-1}>
            <LocalizationProvider dateAdapter={DateAdapter}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                onChange={onDatePickerChange}
                onYearChange={openCalendar}
                renderDay={(day: Date, _, pickersDayProps) => {
                  const key = formatDate(day, 'yyyyMMdd');
                  if (pickersDayProps.outsideCurrentMonth && day.getDate() > 20) {
                    return <div key={key} role="cell" />;
                  }

                  return (
                    <div key={key} role="cell">
                      <PickersDay
                        {...pickersDayProps}
                        onMouseUp={() => pickersDayProps.selected && closeCalendar()}
                        selected={isSameDay(day, value!)}
                      />
                    </div>
                  );
                }}
                renderInput={(params) => <TextField {...params} />}
                showDaysOutsideCurrentMonth
                value={value}
                {...restDatePickerProps}
              />
            </LocalizationProvider>
          </CalendarContainer>
        </Popper>
      </ClickAwayListener>
    </Container>
  );
}

export default DatePickerInput;
